import * as React from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';

const switchVariants = tv({
  slots: {
    base: [
      'p-0 font-normal justify-start',
      'peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      'focus-visible:focus-ring',
      'dark:data-[state=unchecked]:bg-input/80 inline-flex',
      'shrink-0 items-center rounded-full',
      'border border-transparent shadow-xs transition-all',
      'outline-none focus-visible:ring-[3px]',
      'disabled:cursor-not-allowed disabled:opacity-50',
    ],
    thumb: [
      'bg-background dark:data-[state=unchecked]:bg-foreground',
      'dark:data-[state=checked]:bg-primary-foreground',
      'pointer-events-none block rounded-full ring-0',
      'transition-transform data-[state=unchecked]:translate-x-0',
      'data-[state=checked]:translate-x-[calc(100%-2px)]',
    ],
  },
  variants: {
    size: {
      small: {
        base: 'h-4 w-7',
        thumb: 'size-3.5',
      },
      default: {
        base: 'h-[1.15rem] w-8',
        thumb: 'size-4',
      },
      large: {
        base: 'h-6 w-11',
        thumb: 'size-5.5',
      },
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> &
  VariantProps<typeof switchVariants>;

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, size, ...props }, ref) => {
    const styles = switchVariants({ size });

    return (
      <SwitchPrimitive.Root
        ref={ref}
        data-slot="switch"
        className={cn(styles.base(), className)}
        {...props}>
        <SwitchPrimitive.Thumb data-slot="switch-thumb" className={cn(styles.thumb())} />
      </SwitchPrimitive.Root>
    );
  },
);

Switch.displayName = 'Switch';

export default Switch;
